import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { LOSS_QUICK, LOSS_COUNT_QUICK } from '@/mocks/risk';
import { formatVN, Label, NumberInput, QuickChips, Stat, NextStep } from '../ui';

const DD_ACCENT = '#f43f5e';

const tooltipStyle = {
  borderRadius: 12,
  border: '1px solid #ffe4e6',
  fontSize: 12,
  boxShadow: '0 12px 30px -12px rgba(244,63,94,0.25)',
};

const WHEN_CARDS = [
  {
    title: 'Trước chuỗi lệnh',
    desc: 'Hiểu rõ mức sụt giảm tối đa nếu gặp một chuỗi lệnh thua liên tiếp, để biết trước kịch bản xấu nhất có thể xảy ra với tài khoản.',
    example: 'Mỗi lệnh lỗ 2%, thua 10 lệnh liên tiếp → vốn giảm khoảng 18,3%.',
  },
  {
    title: 'Kiểm tra khả năng phục hồi',
    desc: 'Biết chính xác cần bao nhiêu % lợi nhuận để trở lại mức vốn ban đầu — càng sụt sâu, càng khó phục hồi.',
    example: 'Drawdown 18,3% cần phục hồi khoảng 22,4% mới về mức cũ.',
  },
  {
    title: 'Điều chỉnh chiến lược',
    desc: 'Nếu Drawdown vượt quá khả năng chịu đựng, cân nhắc giảm rủi ro mỗi lệnh hoặc cải thiện tỷ lệ thắng của chiến lược.',
    example: 'Giảm mức lỗ từ 2% xuống 1%, thua 10 lệnh chỉ còn khoảng 9,6%.',
  },
];

export default function DrawdownTab({ onNext }: { onNext: () => void }) {
  const [peakBalance, setPeakBalance] = useState(10000);
  const [lossPerTrade, setLossPerTrade] = useState(2);
  const [losses, setLosses] = useState(10);

  const lossRate = lossPerTrade / 100;
  const finalEquity = peakBalance * Math.pow(1 - lossRate, losses);
  const drawdownPct = (1 - Math.pow(1 - lossRate, losses)) * 100;
  const drawdownAmount = peakBalance - finalEquity;
  const recoveryNeeded = finalEquity > 0 ? (drawdownAmount / finalEquity) * 100 : 0;

  const series = useMemo(() => {
    const arr: { trade: number; equity: number }[] = [];
    let eq = peakBalance;
    arr.push({ trade: 0, equity: Math.round(eq) });
    for (let i = 1; i <= losses; i++) {
      eq = eq * (1 - lossRate);
      arr.push({ trade: i, equity: Math.round(eq) });
    }
    return arr;
  }, [peakBalance, lossRate, losses]);

  const milestones = [1, 3, 5, 10].filter((n) => n <= losses);

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
          <i className="ri-information-line text-lg" />
        </span>
        <p className="text-sm leading-relaxed text-slate-600">
          Đây là mô phỏng toán học giả định một chuỗi thua lỗ liên tiếp, không phải dự báo kết quả giao dịch thực tế.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs + chart */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <i className="ri-arrow-down-line text-lg" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-slate-900">Kịch bản sụt giảm</h3>
                <p className="text-xs text-slate-400">Giả định một chuỗi lệnh thua liên tiếp</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label>Vốn đỉnh (Peak Balance)</Label>
                <NumberInput value={peakBalance} onChange={setPeakBalance} suffix="USD" min={0} step={100} />
              </div>

              <div>
                <Label hint={`${formatVN(lossPerTrade, 0, 0)}% / lệnh`}>Mức lỗ mỗi lệnh</Label>
                <QuickChips options={LOSS_QUICK} value={lossPerTrade} onChange={setLossPerTrade} format={(v) => `${formatVN(v, 0, 0)}%`} />
              </div>

              <div>
                <Label hint={`${formatVN(losses, 0, 0)} lệnh`}>Số lệnh thua liên tiếp</Label>
                <NumberInput value={losses} onChange={setLosses} suffix="lệnh" min={1} step={1} />
                <div className="mt-2.5">
                  <QuickChips options={LOSS_COUNT_QUICK} value={losses} onChange={setLosses} />
                </div>
              </div>
            </div>

            <div className="mt-6 h-60">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 10, right: 10, left: -6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="trade" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatVN(v, 0, 0)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${formatVN(v, 0, 0)} USD`, 'Vốn']} labelFormatter={(l) => `Sau ${l} lệnh`} />
                  <Line type="monotone" dataKey="equity" name="Vốn" stroke={DD_ACCENT} strokeWidth={3} dot={false} activeDot={{ r: 5, fill: DD_ACCENT, stroke: '#fff', strokeWidth: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right: result */}
        <div className="lg:col-span-2">
          <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-rose-100 bg-gradient-to-br from-rose-50/70 to-white p-6 md:p-7">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-rose-200/40 blur-3xl" />

            <div className="relative">
              <p className="text-sm font-semibold text-slate-500">Drawdown tối đa</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-mono text-5xl font-bold leading-none tabular-nums text-slate-900 md:text-6xl" style={{ color: DD_ACCENT }}>
                  −{formatVN(drawdownPct, 1, 1)}%
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">Sau {formatVN(losses, 0, 0)} lệnh thua liên tiếp</p>

              <div className="mt-6 grid grid-cols-1 gap-2.5">
                <Stat label="Vốn ban đầu" value={`${formatVN(peakBalance, 0, 0)} USD`} />
                <Stat label="Vốn còn lại" value={`${formatVN(finalEquity, 0, 0)} USD`} />
                <Stat label="Số tiền sụt giảm" value={`−${formatVN(drawdownAmount, 0, 0)} USD`} accent={DD_ACCENT} />
                <Stat label="Cần phục hồi" value={`${formatVN(recoveryNeeded, 1, 1)}%`} accent="#f59e0b" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Vốn tại các mốc thua lỗ</h3>
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          {milestones.map((n) => {
            const eq = peakBalance * Math.pow(1 - lossRate, n);
            return (
              <div key={n} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 text-center">
                <p className="text-[11px] font-medium text-slate-400">Sau {n} lệnh</p>
                <p className="mt-1 font-mono text-lg font-bold tabular-nums text-slate-900">{formatVN(eq, 0, 0)} USD</p>
                <p className="mt-0.5 font-mono text-xs font-semibold" style={{ color: DD_ACCENT }}>
                  −{formatVN((1 - Math.pow(1 - lossRate, n)) * 100, 1, 1)}%
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* When to use */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Ứng dụng chính</h3>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {WHEN_CARDS.map((c) => (
            <div key={c.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
              <span className="block h-1 w-8 rounded-full bg-rose-500" />
              <p className="mt-4 text-sm font-bold text-slate-800">{c.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{c.desc}</p>
              <div className="mt-4 rounded-xl border border-slate-200/70 bg-white/80 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-600">Ví dụ</p>
                <p className="mt-1 font-mono text-[11px] leading-relaxed text-slate-600">{c.example}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <NextStep
        title="Bạn đã biết mức sụt giảm tối đa có thể xảy ra."
        desc="Bước tiếp theo: ước tính nguy cơ phá sản của chiến lược."
        cta="Rủi ro phá sản"
        onClick={onNext}
      />
    </div>
  );
}